/**
 * Character Studio Service
 *
 * Handles persistence and management of characters and their assets.
 * The current rigged PNG flow is stored as one character asset.
 */

import { API_BASE_URL } from '../config/api';
import { getAuthHeaders } from './utils/authTokenUtils';
import {
  CharacterAsset,
  CharacterRig,
  CharacterVoiceReference,
} from '../types/RiggingTypes';

const STORAGE_KEY = 'pixle_rigged_characters';

function generateAssetId(): string {
  return `asset_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

function deriveCharacterName(imageName: string): string {
  return imageName.replace(/\.[^.]+$/, '').trim() || imageName;
}

function createPrimaryAsset(
  rig: Pick<CharacterRig, 'imageUrl' | 'imageName'>
): CharacterAsset {
  return {
    id: generateAssetId(),
    type: 'rigged_png',
    name: rig.imageName,
    url: rig.imageUrl,
    isCanonical: true,
  };
}

function normalizeCharacterRig(rig: CharacterRig): CharacterRig {
  const characterName = rig.name ?? rig.identity?.name ?? deriveCharacterName(rig.imageName);
  const existingAssets = rig.assets && rig.assets.length > 0 ? rig.assets : [createPrimaryAsset(rig)];
  const primaryAssetId = rig.primaryAssetId ?? existingAssets[0]?.id;

  return {
    ...rig,
    name: characterName,
    description: rig.description ?? rig.identity?.description ?? '',
    tags: rig.tags ?? rig.identity?.tags ?? [],
    identity: {
      name: characterName,
      description: rig.description ?? rig.identity?.description ?? '',
      species: rig.identity?.species,
      gender: rig.identity?.gender,
      personality: rig.identity?.personality,
      biography: rig.identity?.biography,
      tags: rig.tags ?? rig.identity?.tags ?? [],
    },
    assets: existingAssets,
    primaryAssetId,
  };
}

function getCurrentUserId(): string | null {
  try {
    const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
    return (
      storedUser?._id ||
      storedUser?.id ||
      localStorage.getItem('userId') ||
      null
    );
  } catch {
    return localStorage.getItem('userId');
  }
}

function normalizeCharacterList(data: unknown): CharacterRig[] {
  if (!Array.isArray(data)) {
    return [];
  }

  return data.map((character) => normalizeCharacterRig(character as CharacterRig));
}

function extractCharacterList(responseData: unknown): CharacterRig[] {
  if (Array.isArray(responseData)) {
    return normalizeCharacterList(responseData);
  }

  if (
    responseData &&
    typeof responseData === 'object' &&
    'characters' in responseData
  ) {
    return normalizeCharacterList(
      (responseData as { characters?: unknown }).characters
    );
  }

  if (responseData && typeof responseData === 'object' && 'data' in responseData) {
    return normalizeCharacterList((responseData as { data?: unknown }).data);
  }

  return [];
}

function getCharacterApiUrl(userId: string): string {
  return `${API_BASE_URL}/character/users/${userId}/characters`;
}

function persistLocalCharacters(characters: CharacterRig[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(characters));
}

/**
 * Fetch the current user's characters from the backend and hydrate local cache.
 */
export async function hydrateRiggedCharactersForCurrentUser(): Promise<
  CharacterRig[]
> {
  const userId = getCurrentUserId();
  if (!userId) {
    return getAllRiggedCharacters();
  }

  try {
    const response = await fetch(getCharacterApiUrl(userId), {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch character studio data');
    }

    const responseData = await response.json();
    const characters = extractCharacterList(responseData);
    persistLocalCharacters(characters);
    return characters;
  } catch (error) {
    console.error('Error hydrating rigged characters:', error);
    return getAllRiggedCharacters();
  }
}

/**
 * Generate a unique ID for a new character rig.
 */
export function generateRigId(): string {
  return `rig_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Get all saved rigged characters.
 */
export function getAllRiggedCharacters(): CharacterRig[] {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    if (data) {
      return JSON.parse(data).map(normalizeCharacterRig);
    }
  } catch (error) {
    console.error('Error loading rigged characters:', error);
  }
  return [];
}

/**
 * Get a single rigged character by ID.
 */
export function getRiggedCharacterById(id: string): CharacterRig | null {
  const characters = getAllRiggedCharacters();
  return characters.find((c) => c.id === id) || null;
}

/**
 * Save a new rigged character.
 */
export async function saveRiggedCharacter(
  rig: CharacterRig
): Promise<CharacterRig> {
  const characters = getAllRiggedCharacters();
  const normalizedRig = normalizeCharacterRig(rig);

  // Check if updating existing
  const existingIndex = characters.findIndex((c) => c.id === normalizedRig.id);

  if (existingIndex >= 0) {
    // Update existing
    characters[existingIndex] = {
      ...normalizedRig,
      updatedAt: new Date().toISOString(),
    };
  } else {
    // Add new
    characters.push({
      ...normalizedRig,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
  }

  try {
    persistLocalCharacters(characters);
  } catch (error) {
    console.error('Error saving rigged character:', error);
    throw new Error('Failed to save character. Storage may be full.');
  }

  const userId = getCurrentUserId();
  if (!userId) {
    return normalizedRig;
  }

  try {
    const response = await fetch(getCharacterApiUrl(userId), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify({ character: normalizedRig }),
    });

    if (!response.ok) {
      throw new Error('Failed to persist character');
    }

    await hydrateRiggedCharactersForCurrentUser();
  } catch (error) {
    console.error('Error syncing rigged character to backend:', error);
  }

  return normalizedRig;
}

/**
 * Delete a rigged character by ID.
 */
export async function deleteRiggedCharacter(id: string): Promise<boolean> {
  const characters = getAllRiggedCharacters();
  const filtered = characters.filter((c) => c.id !== id);

  if (filtered.length === characters.length) {
    return false; // Nothing was removed
  }

  try {
    persistLocalCharacters(filtered);
    const userId = getCurrentUserId();
    if (userId) {
      const response = await fetch(`${getCharacterApiUrl(userId)}/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        await hydrateRiggedCharactersForCurrentUser();
      }
    }
    return true;
  } catch (error) {
    console.error('Error deleting rigged character:', error);
    return false;
  }
}

/**
 * Create a new character rig from an uploaded image.
 * The rig starts with no joints or bones - user adds them in the editor.
 */
export function createNewCharacterRig(
  imageUrl: string,
  imageName: string,
  imageWidth: number,
  imageHeight: number,
  options?: {
    name?: string;
    description?: string;
    tags?: string[];
    gender?: string;
    species?: string;
    personality?: string[];
    voiceReference?: CharacterVoiceReference;
  }
): CharacterRig {
  const now = new Date().toISOString();
  const name = options?.name?.trim() || deriveCharacterName(imageName);
  const description = options?.description?.trim() || '';
  const tags = options?.tags ?? [];
  const gender = options?.gender?.trim() || undefined;
  const species = options?.species?.trim() || undefined;
  const personality = options?.personality ?? [];
  const primaryAsset = {
    id: generateAssetId(),
    type: 'rigged_png' as const,
    name: imageName,
    url: imageUrl,
    isCanonical: true,
  };
  const voiceReference = options?.voiceReference;
  // Persist only the flat stored path on the character (`voiceUrl`, mirroring
  // `imageUrl`). The file name and duration are kept in the `audio_reference`
  // asset so they survive backend serialization.
  const assets: CharacterAsset[] = [primaryAsset];
  if (voiceReference) {
    assets.push({
      id: generateAssetId(),
      type: 'audio_reference',
      name: voiceReference.fileName ?? 'Voice reference',
      url: voiceReference.location,
      metadata: {
        durationSeconds: voiceReference.durationSeconds,
      },
    });
  }

  return {
    id: generateRigId(),
    name,
    description,
    tags,
    identity: {
      name,
      description,
      tags,
      gender,
      species,
      personality,
    },
    assets,
    primaryAssetId: primaryAsset.id,
    voiceUrl: voiceReference?.location,
    imageUrl,
    imageName,
    imageWidth,
    imageHeight,
    joints: [],
    bones: [],
    segments: [],
    createdAt: now,
    updatedAt: now,
  };
}

/** Build a CharacterVoiceReference from a freshly uploaded stored clip. */
export function buildVoiceReference(
  location: string,
  fileName?: string,
  durationSeconds?: number
): CharacterVoiceReference {
  return { location, fileName, durationSeconds };
}

/**
 * Return a shallow copy of `rig` with the given voice clip attached (both as
 * the flat `voiceUrl` field and as an `audio_reference` asset). The returned
 * value still needs to be persisted via `saveRiggedCharacter`.
 */
export function withVoiceReference(
  rig: CharacterRig,
  voiceReference: CharacterVoiceReference
): CharacterRig {
  const assets = [...(rig.assets ?? [])];
  const existingIndex = assets.findIndex(
    (asset) => asset.type === 'audio_reference'
  );

  const voiceAsset: CharacterAsset = {
    id:
      existingIndex >= 0
        ? assets[existingIndex].id
        : generateAssetId(),
    type: 'audio_reference',
    name: voiceReference.fileName ?? 'Voice reference',
    url: voiceReference.location,
    isCanonical: existingIndex >= 0 ? assets[existingIndex].isCanonical : false,
    metadata: { durationSeconds: voiceReference.durationSeconds },
  };

  if (existingIndex >= 0) {
    assets[existingIndex] = voiceAsset;
  } else {
    assets.push(voiceAsset);
  }

  return { ...rig, assets, voiceUrl: voiceReference.location };
}

/**
 * Return a shallow copy of `rig` with the voice reference removed (flat
 * `voiceUrl` field plus any `audio_reference` asset). The returned value still
 * needs to be persisted via `saveRiggedCharacter`.
 */
export function withoutVoiceReference(rig: CharacterRig): CharacterRig {
  const assets = (rig.assets ?? []).filter(
    (asset) => asset.type !== 'audio_reference'
  );

  return { ...rig, assets, voiceUrl: undefined };
}

/**
 * Find the voice reference asset (type `audio_reference`) on a character.
 * Carries the display file name (`asset.name`) and measured duration
 * (`asset.metadata.durationSeconds`) while the character records only the flat
 * stored path in `voiceUrl`.
 */
export function getCharacterVoiceAsset(
  rig: CharacterRig
): CharacterAsset | undefined {
  return (rig.assets ?? []).find((asset) => asset.type === 'audio_reference');
}

/**
 * Check if a character rig has been set up (has joints and bones).
 */
export function isRigComplete(rig: CharacterRig): boolean {
  return rig.joints.length >= 2 && rig.bones.length >= 1;
}

/**
 * Check whether all of a character's editable detail fields have been
 * populated (name, description, gender, species, at least one
 * personality trait, and a voice/audio reference). Drives the
 * "Completed details" stat in the Character Studio so users can see at a
 * glance how many characters are ready to use.
 */
export function isCharacterDetailsComplete(rig: CharacterRig): boolean {
  const identity = rig.identity;
  const name = (rig.name || identity?.name || '').trim();
  const description = (rig.description || identity?.description || '').trim();
  const gender = (identity?.gender ?? '').trim();
  const species = (identity?.species ?? '').trim();
  const personality = identity?.personality ?? [];
  const hasVoice = !!rig.voiceUrl || (rig.assets ?? []).some(
    (asset) => asset.type === 'audio_reference'
  );

  return (
    name.length > 0 &&
    description.length > 0 &&
    gender.length > 0 &&
    species.length > 0 &&
    personality.length > 0 &&
    hasVoice
  );
}

/**
 * Duplicate a rigged character.
 */
export function duplicateRiggedCharacter(id: string): CharacterRig | null {
  const original = getRiggedCharacterById(id);
  if (!original) return null;

  const duplicatedAssets =
    original.assets?.map((asset) => ({
      ...asset,
      id: generateAssetId(),
      isCanonical: asset.isCanonical,
    })) ?? [];
  const duplicatedPrimaryAssetId =
    duplicatedAssets.find((asset) => asset.isCanonical)?.id ??
    duplicatedAssets[0]?.id;

  const duplicate: CharacterRig = {
    ...original,
    id: generateRigId(),
    name: `${original.name ?? deriveCharacterName(original.imageName)} (Copy)`,
    imageName: `${original.imageName} (Copy)`,
    assets: duplicatedAssets.length > 0 ? duplicatedAssets : original.assets,
    primaryAssetId: duplicatedPrimaryAssetId ?? original.primaryAssetId,
    identity: {
      ...(original.identity ?? { name: original.name ?? original.imageName }),
      name: `${original.name ?? deriveCharacterName(original.imageName)} (Copy)`,
    },
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  void saveRiggedCharacter(duplicate);
  return duplicate;
}

/**
 * Export a rigged character as JSON for sharing.
 */
export function exportRiggedCharacter(id: string): string | null {
  const character = getRiggedCharacterById(id);
  if (!character) return null;

  return JSON.stringify(character, null, 2);
}

/**
 * Import a rigged character from JSON.
 */
export function importRiggedCharacter(json: string): CharacterRig | null {
  try {
    const data = JSON.parse(json);

    // Validate required fields
    if (!data.imageUrl || !data.imageName) {
      throw new Error('Invalid character data');
    }

    // Create new ID to avoid conflicts
    const imported: CharacterRig = {
      ...data,
      id: generateRigId(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    void saveRiggedCharacter(imported);
    return imported;
  } catch (error) {
    console.error('Error importing rigged character:', error);
    return null;
  }
}
